import { CalculationParams, CalculationParamsPreset } from '@features/settings/reducer';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import { CALCULATION_PARAMS, shouldHideCalculationParam } from './constants';
import ElectricCalculationParameters from './ElectricCalculationParameters';
import FuelCalculationParameters from './FuelCalculationParameters';

const CalculationParamsDialogBody = ({
    calculationParams,
    calculationParamsPreset,
    overspeedLimit,
    setCalculationParams,
    setCalculationParamsPreset,
    setOverspeedLimit,
    shouldShowHarshKPIs = false,
    shouldShowExcessiveKPIS = false,
    showCalculationParamsPreset = false,
    shouldShowElectricRatings = false,
}: {
    calculationParams: CalculationParams;
    calculationParamsPreset: CalculationParamsPreset;
    overspeedLimit: number;
    setCalculationParams: (newCalculationParams: CalculationParams) => void;
    setCalculationParamsPreset: (updatedPreset: CalculationParamsPreset) => void;
    setOverspeedLimit: (updatedLimit: number) => void;
    shouldShowHarshKPIs?: boolean;
    shouldShowExcessiveKPIS?: boolean;
    showCalculationParamsPreset?: boolean;
    shouldShowElectricRatings?: boolean;
}) => {
    const createWeightChangeHandler = (params: CalculationParams, setParams: (params: CalculationParams) => void) => (
        calcParamName: keyof CalculationParams,
        newWeight?: number
    ) => {
        const currentParam = params[calcParamName];
        if (newWeight === undefined || currentParam.weight === newWeight) return;
        setParams({ ...params, [calcParamName]: { ...currentParam, weight: newWeight } });
    };

    const createSwitchChangeHandler = (
        params: CalculationParams,
        setParams: (params: CalculationParams) => void,
        isMain = false
    ) => (calcParamName: keyof CalculationParams, enabled: boolean) => {
        const currentParam = params[calcParamName];
        if (currentParam.enabled === enabled) return;

        setParams({
            ...params,
            [calcParamName]: {
                ...currentParam,
                enabled,
                weight: enabled ? currentParam.weight : 0,
            },
        });

        if (
            isMain &&
            calculationParamsPreset === CalculationParamsPreset['mixed-fleet'] &&
            enabled &&
            (calcParamName === 'accelerationPedalRating' || calcParamName === 'brakingRating')
        ) {
            setCalculationParamsPreset(CalculationParamsPreset.none);
        }
    };

    const handleFuelCalcParamWeightChange = createWeightChangeHandler(calculationParams, setCalculationParams);
    const handleFuelCalcParamSwitchChange = createSwitchChangeHandler(calculationParams, setCalculationParams, true);
    const handleElectricCalcParamWeightChange = createWeightChangeHandler(calculationParams, setCalculationParams);
    const handleElectricCalcParamSwitchChange = createSwitchChangeHandler(calculationParams, setCalculationParams);

    const handleOverspeedLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        if (!isNaN(value)) setOverspeedLimit(value + 1);
    };

    const handleMixedFleetPreset = () => {
        setCalculationParamsPreset(CalculationParamsPreset['mixed-fleet']);
        setCalculationParams({
            coastingRating: { enabled: true, weight: 33 },
            accelerationPedalRating: { enabled: false, weight: 0 },
            brakingRating: { enabled: false, weight: 0 },
            cruiseControlRating: { enabled: true, weight: 33 },
            overSpeedRating: { enabled: true, weight: 34 },
            harshAccelerationRating: { enabled: true, weight: 0 },
            harshBrakingRating: { enabled: true, weight: 0 },
            excessiveIdlingRating: { enabled: true, weight: 0 },
            recuperationRating: { enabled: true, weight: 25 },
            electricAccelerationPedalRating: { enabled: false, weight: 25 },
            electricCruiseControlRating: { enabled: true, weight: 25 },
            electricOverSpeedRating: { enabled: true, weight: 25 },
        });
    };

    return (
        <>
            {showCalculationParamsPreset && (
                <div data-test="calculationParamsPreset">
                    <div role="menuitem" onClick={handleMixedFleetPreset} style={{ cursor: 'pointer' }}>
                        <FormattedMessage id="administration.rating.parameterAdaptation.preset.mixedFleet" />
                    </div>
                </div>
            )}
            <div className="display-flex width-100pct gap-10">
                <div className="display-flex flex-column gap-5 margin-top-50 width-100pct">
                    {Object.keys(CALCULATION_PARAMS)
                        .filter(key => {
                            if (
                                key.includes('electric') ||
                                (key === CALCULATION_PARAMS.recuperationRating.translation &&
                                    !shouldShowElectricRatings)
                            ) {
                                return false;
                            }
                            return true;
                        })
                        .map(key => {
                            if (shouldHideCalculationParam(key, shouldShowHarshKPIs, shouldShowExcessiveKPIS)) {
                                return <Fragment key={`${key}-hidden`} />;
                            }
                            const calcParamName = key as keyof CalculationParams;
                            const { icon, translation } = CALCULATION_PARAMS[calcParamName];
                            const { enabled } = calculationParams[calcParamName];

                            return (
                                <div
                                    className="display-flex bg-white padding-left-20 padding-10 align-items-center border justify-content-between"
                                    style={{ height: '40px' }}
                                    key={key}
                                >
                                    <div className="display-flex align-items-center">
                                        <span className={`rioglyph rioglyph-${icon} text-size-20 margin-right-10`} />
                                        <FormattedMessage id={`discipline.${translation}`} />
                                    </div>
                                    {key === 'overSpeedRating' && (
                                        <div className="display-flex align-items-center" data-test="overspeedLimit">
                                            <span
                                                className="text-color-dark margin-right-5"
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                <FormattedMessage id="speedLimit" />
                                            </span>
                                            <input
                                                type="number"
                                                className="text-right border rounded"
                                                style={{ width: '48px', height: '28px' }}
                                                min={1}
                                                max={189}
                                                value={overspeedLimit - 1}
                                                disabled={!enabled}
                                                onChange={handleOverspeedLimitChange}
                                            />
                                            <span className="text-color-dark margin-left-5">
                                                <FormattedMessage id="unit.kmph" />
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>
                <FuelCalculationParameters
                    calculationParams={calculationParams}
                    handleCalcParamSwitchChange={handleFuelCalcParamSwitchChange}
                    handleCalcParamWeightChange={handleFuelCalcParamWeightChange}
                    shouldShowHarshKPIs={shouldShowHarshKPIs}
                    shouldShowExcessiveKPIS={shouldShowExcessiveKPIS}
                    shouldShowElectricRatings={shouldShowElectricRatings}
                />
                {shouldShowElectricRatings && (
                    <ElectricCalculationParameters
                        calculationParams={calculationParams}
                        handleCalcParamSwitchChange={handleElectricCalcParamSwitchChange}
                        handleCalcParamWeightChange={handleElectricCalcParamWeightChange}
                        shouldShowHarshKPIs={shouldShowHarshKPIs}
                        shouldShowExcessiveKPIS={shouldShowExcessiveKPIS}
                    />
                )}
            </div>
        </>
    );
};

export default CalculationParamsDialogBody;
